import React, { useMemo, useState } from 'react';
import type { Ingredient, Language } from '../types';
import ChevronDownIcon from './icons/ChevronDownIcon';
import CartIcon from './icons/CartIcon';
import XIcon from './icons/XIcon';
import CookingIngredientRow from './CookingIngredientRow';
import SearchField from './SearchField';
import { getTranslation, isRTL } from '../utils/translations';

interface FuturePlannedItem {
    recipeId: string;
    recipeName: string;
    days: string[];
    totalServings: number;
    totalMeals: number;
}

interface PrepGatherScreenProps {
    consolidatedPrepList: { ingredient: Ingredient; sources: { recipeId: string; recipeName: string; quantity: number }[] }[];
    futurePlannedInfo: FuturePlannedItem[];
    servingsMap: Record<string, number>;
    onUpdateServings: (recipeId: string, newCount: number) => void;
    preppedIngredients: Set<string>;
    onTogglePrepped: (ingredient: Ingredient, sourceRecipeIds: readonly string[]) => void;
    onAddIngredientsToCart?: (ingredients: any[], label: string) => Promise<void>;
    language?: Language;
    isHidden?: boolean;
    mealLabel: string;
    sessionDateString?: string;
    onBack: () => void;
}

const PrepGatherScreen: React.FC<PrepGatherScreenProps> = ({
    consolidatedPrepList,
    futurePlannedInfo,
    servingsMap,
    onUpdateServings,
    preppedIngredients,
    onTogglePrepped,
    onAddIngredientsToCart,
    language,
    isHidden = false,
    mealLabel,
    sessionDateString,
    onBack,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    const [isPrepTipExpanded, setIsPrepTipExpanded] = useState(true);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [ingredientSearchQuery, setIngredientSearchQuery] = useState('');

    const filteredPrepList = useMemo(() => {
        const query = ingredientSearchQuery.trim().toLowerCase();
        if (!query) return consolidatedPrepList;

        return consolidatedPrepList.filter((item) => {
            const ingredientText = [
                item.ingredient.name,
                item.ingredient.unit,
                item.ingredient.section,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            const sourcesText = item.sources.map((source) => source.recipeName).join(' ').toLowerCase();
            return ingredientText.includes(query) || sourcesText.includes(query);
        });
    }, [consolidatedPrepList, ingredientSearchQuery]);

    const allSelectableKeys = useMemo(
        () => filteredPrepList.map((item) => `${item.ingredient.name}-${item.ingredient.unit}`),
        [filteredPrepList],
    );
    const allSelected = allSelectableKeys.length > 0 && allSelectableKeys.every((k) => selectedKeys.has(k));

    const toggleSelection = (key: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleSelectKeys = (keys: string[]) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (keys.length > 0 && keys.every((k) => next.has(k))) keys.forEach((k) => next.delete(k));
            else keys.forEach((k) => next.add(k));
            return next;
        });
    };

    const toggleSelectionMode = () => {
        setIsSelectionMode((prev) => !prev);
        setSelectedKeys(new Set());
    };

    const handleAddToCart = async () => {
        if (!onAddIngredientsToCart || selectedKeys.size === 0) return;
        setIsAddingToCart(true);
        try {
            const items = consolidatedPrepList
                .filter((item) => selectedKeys.has(`${item.ingredient.name}-${item.ingredient.unit}`))
                .map((item) => ({
                    ...item.ingredient,
                    sources: item.sources.map((src) => ({
                        originName: src.recipeName,
                        originType: 'day' as const,
                        originId: sessionDateString,
                        quantity: src.quantity,
                    })),
                }));
            if (items.length > 0) await onAddIngredientsToCart(items, mealLabel);
            setIsSelectionMode(false);
            setSelectedKeys(new Set());
        } catch (error) {
            console.error('Failed to add to cart', error);
        } finally {
            setIsAddingToCart(false);
        }
    };

    return (
        <div className={`fixed inset-0 bg-bg-cream dark:bg-slate-950 z-50 flex-col font-rubik overflow-hidden transition-colors duration-200 ${isHidden ? 'hidden' : 'flex'}`} dir={rtl ? 'rtl' : 'ltr'}>
            {/* Back to Menu */}
            <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-stone-200 dark:border-slate-800 flex-shrink-0 px-4">
                <div className="max-w-2xl mx-auto">
                    <button
                        onClick={onBack}
                        className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium py-4 flex items-center gap-1.5 transition-colors"
                    >
                        <span>{rtl ? '›' : '‹'}</span>
                        <span>{t('backToMenu')}</span>
                    </button>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-grow overflow-y-auto scrollbar-none">
                <div className="max-w-2xl mx-auto px-4 py-8 pb-32">
                    <div className="animate-fade-in">
                        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 shadow-sm border border-stone-100 dark:border-slate-800 mb-8 text-center">
                            <h3 className="text-3xl font-black text-brand-sage tracking-tight mb-2">{t('prepTab')}</h3>
                            <p className="text-slate-400 dark:text-slate-500 font-medium">{t('prepGatherIngredients')}</p>
                        </div>

                        {futurePlannedInfo.length > 0 && (
                            <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-[2rem] p-6 mb-8 transition-all overflow-hidden">
                                <button
                                    onClick={() => setIsPrepTipExpanded(!isPrepTipExpanded)}
                                    className="w-full text-sm font-black uppercase tracking-widest text-amber-800 dark:text-amber-400 mb-2 flex items-center justify-between hover:opacity-80 transition-opacity"
                                >
                                    <span className="flex items-center gap-2">
                                        <span>💡</span> {t('futureMealsNoticeTitle')}
                                    </span>
                                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${isPrepTipExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {isPrepTipExpanded && (
                                    <ul className="space-y-3 mt-4">
                                        {futurePlannedInfo.map(({ recipeId, recipeName, days, totalServings, totalMeals }) => {
                                            const currentServings = servingsMap[recipeId] ?? 0;
                                            const isAlreadyTotal = currentServings === totalServings;
                                            return (
                                                <li key={recipeId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white/60 dark:bg-slate-950/40 border border-amber-200/20 rounded-2xl shadow-sm">
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-amber-500 mt-0.5">•</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                                            {t('futureMealsPlannedHint')
                                                                .replace('{recipe}', recipeName)
                                                                .replace('{days}', days.join(', '))
                                                                .replace('{totalServings}', String(totalServings))
                                                                .replace('{totalMeals}', String(totalMeals))}
                                                        </span>
                                                    </div>
                                                    {!isAlreadyTotal && (
                                                        <button
                                                            onClick={() => onUpdateServings(recipeId, totalServings)}
                                                            className="flex-shrink-0 px-3.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-black uppercase tracking-wider transition-colors shadow-sm self-end sm:self-center"
                                                        >
                                                            {t('scaleToTotalServings').replace('{count}', String(totalServings))}
                                                        </button>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div className="space-y-6">
                            <div className="flex items-center justify-between px-2">
                                <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('ingredients')}</h3>
                                <div className="flex items-center gap-2">
                                    {isSelectionMode && (
                                        <button
                                            onClick={() => toggleSelectKeys(allSelectableKeys)}
                                            className="text-[10px] font-black uppercase tracking-widest text-brand-orange hover:text-orange-700 dark:hover:text-orange-300 transition-colors px-2 py-1"
                                        >
                                            {allSelected ? t('deselectAll') : t('selectAll')}
                                        </button>
                                    )}
                                    {onAddIngredientsToCart && (
                                        <button
                                            onClick={toggleSelectionMode}
                                            className={`p-2.5 rounded-full transition-all shadow-sm ${isSelectionMode ? 'bg-orange-50 dark:bg-orange-900/30 text-brand-orange ring-2 ring-brand-orange/20' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                                            title={isSelectionMode ? t('cancel') : t('addToActive')}
                                        >
                                            {isSelectionMode ? <XIcon className="w-5 h-5" /> : <CartIcon className="w-5 h-5" />}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <SearchField
                                value={ingredientSearchQuery}
                                onChange={setIngredientSearchQuery}
                                placeholder={t('searchIngredients')}
                                clearLabel={t('clearSearch')}
                                dir={rtl ? 'rtl' : 'ltr'}
                                inputClassName="py-3.5 dark:bg-slate-900 shadow-sm"
                            />
                            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm divide-y divide-stone-50 dark:divide-slate-800">
                                {filteredPrepList.length === 0 ? (
                                    <div className="px-6 py-10 text-center text-slate-400 dark:text-slate-500 font-medium">
                                        {t('noResults')}
                                    </div>
                                ) : filteredPrepList.map((item) => {
                                    const selKey = `${item.ingredient.name}-${item.ingredient.unit}`;
                                    const prepKey = selKey;
                                    const sourcesLabel = item.sources.map((s) => s.recipeName).join(', ');
                                    return (
                                        <CookingIngredientRow
                                            key={selKey}
                                            ingredient={item.ingredient}
                                            isStruck={preppedIngredients.has(prepKey)}
                                            isSelectionMode={isSelectionMode}
                                            isSelected={selectedKeys.has(selKey)}
                                            sourcesLabel={sourcesLabel}
                                            t={t}
                                            rtl={rtl}
                                            onToggle={() => (
                                                isSelectionMode
                                                    ? toggleSelection(selKey)
                                                    : onTogglePrepped(
                                                        item.ingredient,
                                                        item.sources.map((source) => source.recipeId),
                                                    )
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Selection Bar */}
            {isSelectionMode && onAddIngredientsToCart && (
                <div className="absolute bottom-4 left-4 right-4 max-w-2xl mx-auto bg-slate-900 dark:bg-slate-800 text-white p-4 rounded-2xl shadow-2xl flex justify-between items-center z-[210] animate-fade-in border border-slate-700">
                    <span className="font-bold text-sm px-2">{selectedKeys.size} {t('selected')}</span>
                    <div className="flex gap-3">
                        <button onClick={toggleSelectionMode} className="px-4 py-2 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-white transition-colors">
                            {t('cancel')}
                        </button>
                        <button
                            onClick={handleAddToCart}
                            disabled={isAddingToCart || selectedKeys.size === 0}
                            className="bg-brand-orange text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-orange/20 active:scale-95 transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                        >
                            {isAddingToCart ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <CartIcon className="w-4 h-4" />
                            )}
                            {t('addToActive')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrepGatherScreen;
